import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/AuthContext'

export default function SavedSearches({ onApply, currentFilters = {} }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [searchName, setSearchName] = useState('')

  const { data: savedSearches = [] } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('saved_searches')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
    enabled: !!user,
  })

  const saveMutation = useMutation({
    mutationFn: async ({ name, filters }) => {
      const { data, error } = await supabase
        .from('saved_searches')
        .insert({
          name,
          query_params: filters,
        })
        .select()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['saved-searches'])
      setShowSaveDialog(false)
      setSearchName('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('saved_searches')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['saved-searches'])
    },
  })

  const handleSave = () => {
    if (searchName.trim()) {
      saveMutation.mutate({ name: searchName, filters: currentFilters })
    }
  }

  const handleApply = (search) => {
    if (onApply) {
      onApply(search.query_params)
    }
  }

  const handleDelete = (id, e) => {
    e.stopPropagation()
    if (confirm('Delete this saved search?')) {
      deleteMutation.mutate(id)
    }
  }

  if (!user) return null

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-secondary-900">Saved Searches</h3>
        <button
          onClick={() => setShowSaveDialog(true)}
          className="text-sm text-primary-600 hover:text-primary-700"
        >
          + Save Current
        </button>
      </div>

      {savedSearches.length === 0 ? (
        <p className="text-sm text-secondary-500 py-4">No saved searches yet</p>
      ) : (
        <div className="space-y-2">
          {savedSearches.map((search) => (
            <div
              key={search.id}
              className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 cursor-pointer transition-colors"
              onClick={() => handleApply(search)}
            >
              <div className="flex-1">
                <div className="font-medium text-secondary-900">{search.name}</div>
                <div className="text-xs text-secondary-600 mt-1">
                  {Object.keys(search.query_params || {}).length} filters
                </div>
              </div>
              <button
                onClick={(e) => handleDelete(search.id, e)}
                className="text-red-600 hover:text-red-700 p-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Save Search</h3>
            <div className="mb-4">
              <label className="label">Search Name</label>
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="e.g., Auckland apartments under $1M"
                className="input"
                autoFocus
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowSaveDialog(false)
                  setSearchName('')
                }}
                className="btn-outline px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!searchName.trim() || saveMutation.isPending}
                className="btn-primary px-4 py-2"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function SavedSearchesProvider({ filters, children }) {
  return (
    <>
      {children(filters)}
    </>
  )
}
